import CheckCircleIcon from "@assets/images/check-circle.svg";
import EOrderingLogo from "@assets/images/eOrdering.png";
import SCGP_Color from "@assets/images/SCGP-color.svg";
import { Box } from "@material-ui/core";
import { isStaffRole } from "@saleor/auth/hooks/useUserRole";
import Container from "@saleor/components/Container";
import HeadColored from "@saleor/components/HeadColored";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import {
  getNotRefPreviewChangeOrderUrl,
  getNotRefPreviewOrderUrl,
  NotRefOrderQueryParams,
  NotRefOrderSortField
} from "@saleor/createOrChangeNotRefContract/urls";
import { useDownloadOrdersPdfMutation } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import { useLocalPaginationState } from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginationLocal from "@saleor/hooks/usePaginationLocal";
import { ListViews } from "@saleor/types";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import { parse as parseQs } from "qs";
import React, { useEffect, useMemo } from "react";

import CashSaleStatus from "./CashSaleStatus";
import PreviewOrderNotRefFooter from "./Footer";
import PreviewOrderNotRefHeader from "./Header";
import useStyles from "./styles";
import PreviewOrderNotRefTable from "./Table";
import { useDataPreview } from "./useDataPreview";

export interface PreviewOrderNotRefProps {
  orderId: string;
  params: NotRefOrderQueryParams;
  isChangeFlow: boolean;
}

const PreviewOrderNotRef: React.FC<PreviewOrderNotRefProps> = ({
  orderId,
  params,
  isChangeFlow
}) => {
  const classes = useStyles();
  const navigate = useNavigator();
  const qs = location?.search ? parseQs(location.search.substr(1)) : {};

  const [, dispatchAppState] = useAppState();

  const isStaff = isStaffRole();

  useEffect(() => {
    dispatchAppState({
      type: "setFullScreen",
      payload: {
        value: true
      }
    });
    return () => {
      dispatchAppState({
        type: "setFullScreen",
        payload: {
          value: false
        }
      });
    };
  }, []);

  useEffect(() => {
    if (!isStaff) {
      const handlePopState = () => {
        window.location.href = "/dashboard/sold-to";
      };
      window.history.pushState(null, null, window?.location?.pathname);
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, []);

  const { updateListSettings, settings } = useListSettings(
    ListViews.FILTER_ORDER
  );

  const [
    valuesPaginationState,
    _setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);

  const { dataPreview, loadingPreviewOrder } = useDataPreview(
    orderId,
    params,
    valuesPaginationState,
    settings
  );

  const [downloadPdfOrder] = useDownloadOrdersPdfMutation();

  useEffect(() => {
    updateListSettings("rowNumber", 50);
  }, []);

  const previewOrderLines =
    useMemo(() => {
      if (dataPreview) {
        const paramSort = getSortParams(params);
        const listToSort = [
          ...dataPreview.previewDomesticOrder.previewItemData
        ];
        switch (paramSort.sort) {
          case NotRefOrderSortField.itemNo:
            if (paramSort.asc) {
              listToSort.sort(
                (itemA, itemB) => Number(itemA.itemNo) - Number(itemB.itemNo)
              );
            } else {
              listToSort.sort(
                (itemA, itemB) => Number(itemB.itemNo) - Number(itemA.itemNo)
              );
            }
        }
        return listToSort;
      }
      return [];
    }, [dataPreview?.previewDomesticOrder?.previewItemData, params]) || [];

  const {
    currentData,
    next,
    prev,
    toFirst,
    toLast,
    disableNext,
    disablePrev,
    lastestPageItemNumber
  } = usePaginationLocal(previewOrderLines, settings.rowNumber);

  const handleSort = field => {
    toFirst();
    const url = isChangeFlow
      ? getNotRefPreviewChangeOrderUrl(orderId)(
          getSortUrlVariables(field, params)
        )
      : getNotRefPreviewOrderUrl(orderId)(getSortUrlVariables(field, params));

    if (url) {
      navigate(url, { replace: true });
    }
  };

  return (
    <>
      <HeadColored fixedTop />
      <Container className={classes.container}>
        <Box className={classes.pageHeader}>
          <PageHeader title="Preview Order" />
          <div className={classes.headerPageLogo}>
            <img
              src={SCGP_Color}
              alt="SCGP-logo"
              className={classes.scgpLogo}
            />
            <img
              src={EOrderingLogo}
              alt="EOrdering-logo"
              className={classes.eOrderingLogo}
            />
          </div>
        </Box>
        <Box className={classes.headerTitle}>
          <div>
            <img
              className={classes.checkCircleIcon}
              src={CheckCircleIcon}
              alt="success"
            />
            <MyTypography variant="body1" className={classes.headerContent}>
              {qs?.rootPage
                ? "เปลี่ยนคำสั่งซื้อเสร็จสมบูรณ์"
                : "สร้างคำสั่งซื้อเสร็จสมบูรณ์"}
            </MyTypography>
          </div>
          <CashSaleStatus status={qs?.cashSaleStatus} />
        </Box>
        <PreviewOrderNotRefHeader previewOrder={dataPreview} />
        {(!loadingPreviewOrder || previewOrderLines) && (
          <PreviewOrderNotRefTable
            previewOrderLines={currentData()}
            disabled={loadingPreviewOrder}
            pageInfo={{
              hasPreviousPage: !disablePrev(),
              hasNextPage: !disableNext()
            }}
            onNextPage={next}
            onPreviousPage={prev}
            onUpdateListSettings={updateListSettings}
            onRowClick={null}
            sort={getSortParams(params)}
            onSort={handleSort}
            settings={settings}
            totalCount={
              dataPreview?.previewDomesticOrder?.previewItemData.length
            }
            latestPageItemNumber={lastestPageItemNumber}
            onPreviousFirstPage={toFirst}
            onNextLastPage={toLast}
          />
        )}
        <PreviewOrderNotRefFooter
          onDownloadPdf={downloadPdfOrder}
          previewOrder={dataPreview}
        />
      </Container>
    </>
  );
};

export default PreviewOrderNotRef;
